/*
 * Marketplace publishing — who may open the seller workspace.
 *
 * Mirrors services/marketplaceReview.js. Publishing is invite-only during the
 * pilot: a verified publisher is given the seller workspace by a grant, never
 * by a role. Granted publishers can create private drafts and submit them for
 * BlueBear review through the existing marketplace API client.
 *
 * Two projections light it up, both from bluebear-gateway:
 *  - `publish_marketplace_offers` in `user.permissions` (per user), or
 *  - `marketplace_publish` in `user.features` (per tenant).
 */

import { summarizeEarnings } from './marketplaceReview';
import { safePublicLaunchUrl } from './marketplaceOffer';

/** The gateway permission that governs the seller workspace. */
export const MARKETPLACE_PUBLISH_PERMISSION = 'publish_marketplace_offers';
/** The tenant-feature key, for when the grant is projected that way instead. */
export const MARKETPLACE_PUBLISH_FEATURE = 'marketplace_publish';

/**
 * May this viewer see the seller workspace?
 *
 * Deliberately NO role fallback. The gateway keeps
 * `publish_marketplace_offers` out of ROLE_PERMISSIONS on purpose — handing it
 * to every `tenant`-role user would turn an invite-only pilot into open
 * self-service publishing — so the UI does not widen it either. Without the
 * grant, /marketplace/sell shows the application panel, not the workspace.
 */
export function canPublishMarketplaceOffers(user) {
  if (!user) return false;
  const permissions = user.permissions || user.scopes || [];
  if (Array.isArray(permissions) && permissions.includes(MARKETPLACE_PUBLISH_PERMISSION)) {
    return true;
  }
  const features = user.features || user.tenant?.features || [];
  if (Array.isArray(features) && features.includes(MARKETPLACE_PUBLISH_FEATURE)) {
    return true;
  }
  return false;
}

// ── Publisher earnings ──────────────────────────────────────────────────────

/*
 * Earnings, access requests, and seller offers use the existing
 * publisher-scoped marketplace client.
 */
export async function loadPublisherEarnings(client) {
  try {
    const res = await client.listEarnings();
    const summary = summarizeEarnings(res?.data);
    return { status: 'ready', accruedUsd: summary.accruedUsd, paidUsd: summary.paidUsd, count: summary.rows.length };
  } catch (err) {
    const status = err?.response?.status;
    return {
      status: 'unavailable',
      accruedUsd: null,
      paidUsd: null,
      count: null,
      reason: status === 401 || status === 403
        ? 'Your session is not scoped to a publisher tenant.'
        : err?.response?.data?.error || err?.message || 'Earnings could not be loaded.',
    };
  }
}

const cleanText = (value) => String(value || '').trim();

/** Normalize the publisher inbox without leaking gateway-only fields into UI code. */
export function normalizePublisherAccessRequests(payload) {
  const source = Array.isArray(payload?.access_requests) ? payload.access_requests : [];
  const rows = source
    .map((row) => ({
      id: cleanText(row?.id),
      offerSlug: cleanText(row?.offer_slug),
      offerTitle: cleanText(row?.offer_title),
      contactEmail: cleanText(row?.contact_email).toLowerCase(),
      status: cleanText(row?.status),
      checkoutUrl: cleanText(row?.checkout_url) || null,
      createdAt: cleanText(row?.created_at),
    }))
    .filter((row) => row.id && row.offerSlug && row.contactEmail);

  return {
    rows,
    requestCount: rows.length,
    uniqueBuyerCount: new Set(rows.map((row) => row.contactEmail)).size,
  };
}

export async function loadPublisherAccessRequests(client) {
  try {
    const res = await client.listAccessRequests({ limit: 50 });
    return { status: 'ready', ...normalizePublisherAccessRequests(res?.data) };
  } catch (err) {
    const status = err?.response?.status;
    return {
      status: 'unavailable',
      rows: [],
      requestCount: null,
      uniqueBuyerCount: null,
      reason: status === 401 || status === 403
        ? 'Your session is not scoped to a publisher tenant.'
        : err?.response?.data?.error || err?.message || 'Access requests could not be loaded.',
    };
  }
}

// ── Publisher application (lead capture) ────────────────────────────────────

/*
 * There is no publisher-application route and none is being added for the
 * pilot. The application rides the existing POST /contact, whose handler
 * already accepts `requestType`, `productArea`, `sourcePage` and `topic`
 * verbatim (bluebear-gateway src/routes/contact.routes.ts), so an application
 * is distinguishable downstream without a schema change.
 */
export function normalizePublisherOffers(payload) {
  const rows = Array.isArray(payload?.offers) ? payload.offers : [];
  return rows
    .map((row) => ({
      slug: cleanText(row?.slug),
      offerType: cleanText(row?.offer_type),
      title: cleanText(row?.title),
      summary: cleanText(row?.summary),
      kind: cleanText(row?.kind),
      status: cleanText(row?.status),
      version: cleanText(row?.version),
      pricing: row?.pricing && typeof row.pricing === 'object' ? row.pricing : {},
      sizes: Array.isArray(row?.sizes) ? row.sizes : [],
      publicUrl: cleanText(row?.public_url),
      manifestUrl: cleanText(row?.manifest_url),
      skillUrl: cleanText(row?.skill_url) || null,
      updatedAt: cleanText(row?.updated_at),
    }))
    .filter((row) => row.slug && row.title && row.status);
}

export async function loadPublisherOffers(client) {
  try {
    const res = await client.listPublisherOffers();
    return { status: 'ready', rows: normalizePublisherOffers(res?.data) };
  } catch (err) {
    const status = err?.response?.status;
    return {
      status: 'unavailable',
      rows: [],
      reason: status === 401 || status === 403
        ? 'Your session is not allowed to manage marketplace offers.'
        : err?.response?.data?.error || err?.message || 'Offers could not be loaded.',
    };
  }
}

export const slugifyOfferTitle = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 120);

export function buildPublisherOfferDraftPayload(form = {}, offerTypes = []) {
  const title = cleanText(form.title);
  const summary = cleanText(form.summary);
  const slug = cleanText(form.slug || slugifyOfferTitle(title));
  const offerType = offerTypes.find((type) => type.id === form.offerType);
  const delivery = cleanText(form.delivery);
  const pricingModel = cleanText(form.pricingModel);
  const amount = Number(form.amountUsd || 0);
  const endpoint = cleanText(form.executionUrl);
  const publicLaunchUrl = cleanText(form.publicLaunchUrl);
  const publicLaunchLabel = cleanText(form.publicLaunchLabel);
  const autoDispatch = form.autoDispatch === true;
  const runner = cleanText(form.runnerSlug);
  const tool = cleanText(form.toolName);

  if (!offerType) return { ok: false, error: 'Choose an available offer type.' };
  if (!title) return { ok: false, error: 'Name the offer.' };
  if (!summary) return { ok: false, error: 'Describe what the buyer gets.' };
  if (!slug || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
    return { ok: false, error: 'Use a simple link name with lowercase letters, numbers, and hyphens.' };
  }
  if (!delivery) return { ok: false, error: 'Choose where the offer is delivered.' };
  if (!pricingModel) return { ok: false, error: 'Choose a pricing model.' };
  if (!Number.isFinite(amount) || amount < 0) return { ok: false, error: 'Enter a valid price.' };
  if (endpoint && !isHttpUrl(endpoint)) return { ok: false, error: 'Enter a valid https:// endpoint.' };
  if (publicLaunchUrl && !safePublicLaunchUrl(publicLaunchUrl)) {
    return { ok: false, error: 'Enter a valid https:// public launch URL.' };
  }
  if (publicLaunchLabel && !publicLaunchUrl) {
    return { ok: false, error: 'Add a public launch URL for the button label.' };
  }
  if (publicLaunchLabel.length > 80) {
    return { ok: false, error: 'Keep the public button label to 80 characters or fewer.' };
  }
  if (autoDispatch && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(runner)) {
    return { ok: false, error: 'Choose the registered MCP server slug that runs this offer.' };
  }
  if (autoDispatch && !/^[A-Za-z0-9_.:-]+$/.test(tool)) {
    return { ok: false, error: 'Enter the MCP tool that runs this offer.' };
  }

  return {
    ok: true,
    payload: {
      slug,
      offer_type: offerType.id,
      title,
      summary,
      version: '0.1.0',
      kind: offerType.id,
      pricing: { model: pricingModel, amount_usd: amount, currency: 'USD' },
      sizes: amount > 0 ? [{ code: 'standard', amount_usd: amount }] : [],
      access: {
        delivery,
        ...(publicLaunchUrl ? {
          launch_url: safePublicLaunchUrl(publicLaunchUrl),
          launch_label: publicLaunchLabel || 'Try this offer',
        } : {}),
      },
      execution: {
        delivery,
        transport: delivery,
        auto_dispatch: autoDispatch,
        ...(autoDispatch ? { runner, tool } : {}),
        ...(endpoint ? { endpoint } : {}),
      },
    },
  };
}

export async function savePublisherOfferDraft(client, form, offerTypes) {
  const built = buildPublisherOfferDraftPayload(form, offerTypes);
  if (!built.ok) return { status: 'invalid', message: built.error };
  try {
    const res = await client.createPublisherOffer(built.payload);
    return { status: 'saved', message: 'Draft saved.', offer: res?.data };
  } catch (err) {
    return { status: 'error', message: err?.response?.data?.error || err?.message || 'The draft could not be saved.' };
  }
}

/** Edit descriptive fields only; the gateway preserves pricing and runtime metadata. */
export async function updatePublisherOfferDraft(client, draft, form) {
  if (!draft?.slug || !['draft', 'rejected'].includes(draft.status)) {
    return { status: 'invalid', message: 'Only a draft or rejected offer can be edited.' };
  }
  const title = cleanText(form.title);
  const summary = cleanText(form.summary);
  if (!title || !summary) {
    return { status: 'invalid', message: 'Name the offer and describe what the buyer gets.' };
  }
  try {
    const res = await client.updatePublisherOffer(draft.slug, { title, summary });
    return { status: 'saved', message: 'Changes saved.', offer: res?.data };
  } catch (err) {
    return { status: 'error', message: err?.response?.data?.error || err?.message || 'Changes could not be saved.' };
  }
}

export async function submitPublisherOffer(client, slug) {
  try {
    const res = await client.submitPublisherOffer(slug);
    return { status: 'submitted', offer: res?.data };
  } catch (err) {
    return { status: 'error', message: err?.response?.data?.error || err?.message || 'The offer could not be submitted.' };
  }
}

export const PUBLISHER_APPLICATION_REQUEST_TYPE = 'marketplace-publisher-application';
export const PUBLISHER_APPLICATION_PRODUCT_AREA = 'marketplace';
export const PUBLISHER_APPLICATION_SOURCE_PAGE = '/marketplace/sell';
export const PUBLISHER_APPLICATION_TOPIC = 'Marketplace publisher application';

const isHttpUrl = (value) => /^https?:\/\/\S+$/i.test(String(value || '').trim());

/**
 * Build the /contact payload from the application form.
 *
 * `offerTypes` is the current gateway configuration; the
 * chosen type must be one of them so the message never names an artifact the
 * marketplace does not sell.
 */
export function buildPublisherApplicationPayload(form = {}, offerTypes = []) {
  const name = String(form.name || '').trim();
  const email = String(form.email || '').trim();
  const company = String(form.company || '').trim();
  const link = String(form.link || '').trim();
  const notes = String(form.notes || '').trim();
  const offerType = offerTypes.find((type) => type.id === form.offerType) || null;

  if (!name) return { ok: false, error: 'Tell us your name.' };
  if (!email || !email.includes('@')) return { ok: false, error: 'Enter the email address we should reply to.' };
  if (!offerType) return { ok: false, error: 'Choose an available offer type.' };
  if (!link || !isHttpUrl(link)) {
    return { ok: false, error: 'Add a link (https://…) to what you want to sell — a repo, docs, or a live tool.' };
  }

  const message = [
    `Applying to publish a ${offerType.label} on the BlueBear Marketplace.`,
    `Link: ${link}`,
    company ? `Company: ${company}` : null,
    notes ? `Notes: ${notes}` : null,
  ].filter(Boolean).join('\n');

  return {
    ok: true,
    payload: {
      offer_type: offerType.id,
      name,
      email,
      company,
      topic: PUBLISHER_APPLICATION_TOPIC,
      requestType: PUBLISHER_APPLICATION_REQUEST_TYPE,
      productArea: PUBLISHER_APPLICATION_PRODUCT_AREA,
      sourcePage: PUBLISHER_APPLICATION_SOURCE_PAGE,
      message,
    },
  };
}

/**
 * Submit an application through the shared contact binding.
 *
 * `client` is `contact` from services/api (anything with `submit(payload)`),
 * injected so the state machine can be tested without axios.
 */
export async function submitPublisherApplication(client, form, offerTypes) {
  const built = buildPublisherApplicationPayload(form, offerTypes);
  if (!built.ok) return { status: 'invalid', message: built.error };
  try {
    await client.submit(built.payload);
    return {
      status: 'sent',
      message: 'Application received. We reply by email while the pilot is invite-only.',
    };
  } catch (err) {
    const status = err?.response?.status;
    if (status === 429) {
      return { status: 'error', message: 'Too many submissions right now. Please try again in a few minutes.' };
    }
    return {
      status: 'error',
      message: err?.response?.data?.error || err?.message || 'Your application could not be sent.',
    };
  }
}
